/**
 * Chamber Console view contracts — what the operator's screens actually receive.
 *
 * Deliberately separate from the integrator contract: the console can change shape in a
 * release, whereas `/api/v1` is a published contract third parties depend on. Coupling
 * them would mean a console improvement becomes a breaking API change.
 */
import { z } from 'zod';
import { checkSourceSchema, checkStatusSchema, deliveryModeSchema, documentStatusSchema, intakeChannelSchema, messageTypeSchema, submissionTypeSchema, } from './enums.js';
import { extractionPanelSchema } from './extraction.js';
import { dateRangeSchema, pageQuerySchema } from './pagination.js';
export const documentQueueQuerySchema = pageQuerySchema.merge(dateRangeSchema.innerType()).extend({
    status: z.string().optional(),
    documentTypeId: z.string().optional(),
    submissionType: submissionTypeSchema.optional(),
    deliveryMode: deliveryModeSchema.optional(),
    organisationId: z.string().optional(),
    integratorId: z.string().optional(),
    intakeChannel: intakeChannelSchema.optional(),
    processedByUserId: z.string().optional(),
    /** `overdue` | `today` | `3days` | `week` — SLA ageing against requestedTargetDate. */
    ageing: z.string().optional(),
});
export const documentRowSchema = z.object({
    id: z.string(),
    senderReference: z.string(),
    organisationName: z.string(),
    organisationId: z.string().nullable(),
    /**
     * False when the matched exporter has been made inactive: no certificate can be issued
     * for them until they are reactivated. Null when no registered exporter was matched.
     */
    organisationActive: z.boolean().nullable(),
    documentTypeCode: z.string(),
    documentTypeName: z.string(),
    submissionType: submissionTypeSchema,
    messageType: messageTypeSchema,
    status: documentStatusSchema,
    deliveryMode: deliveryModeSchema,
    certificateNo: z.string().nullable(),
    /**
     * What PRINTS on the certificate — a calendar date, not an instant, and not the same
     * thing as `processedAt`. A back-dated certificate is approved today and dated the day
     * the shipment sailed, and the screen has to show the one that is on the document.
     */
    certificateDate: z.string().nullable(),
    receivedAt: z.string(),
    requestedTargetDate: z.string().nullable(),
    processedAt: z.string().nullable(),
    processedByName: z.string().nullable(),
    destinationCountry: z.string(),
    goodsLineCount: z.number().int(),
    supportingDocumentCount: z.number().int(),
    integratorName: z.string().nullable(),
    /**
     * How it arrived, and who from. `intakeIdentity` is what the operator reads — the
     * integrator's name, or "Uploaded by …" / "Emailed by …". Always present: there is no
     * such thing as a document that arrived by no means.
     */
    intakeChannel: intakeChannelSchema,
    intakeIdentity: z.string(),
    /** Negative = overdue. Null = no target date was supplied. */
    daysToTarget: z.number().int().nullable(),
    /** Worst check status across the document, for an at-a-glance queue indicator. */
    checkSummary: checkStatusSchema.nullable(),
});
export const checkResultSchema = z.object({
    id: z.string(),
    checkType: z.string(),
    title: z.string(),
    status: checkStatusSchema,
    source: checkSourceSchema,
    message: z.string(),
    fieldRef: z.string().nullable(),
    /** Page + rect for the bounding-box overlay on the preview, when extraction gives it. */
    location: z
        .object({
        page: z.number().int(),
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
    })
        .nullable()
        .optional(),
    expected: z.string().nullable().optional(),
    found: z.string().nullable().optional(),
    createdAt: z.string(),
});
export const checkPanelSchema = z.object({
    /** QUEUED | RUNNING | COMPLETE | UNAVAILABLE — the panel says which, plainly. */
    state: z.enum(['QUEUED', 'RUNNING', 'COMPLETE', 'UNAVAILABLE']),
    /** True when the semantic layer ran. False = rule engine only, and the panel says so. */
    semanticLayerRan: z.boolean(),
    unavailableReason: z.string().nullable(),
    results: z.array(checkResultSchema),
    ranAt: z.string().nullable(),
    summary: z.object({
        pass: z.number().int(),
        warning: z.number().int(),
        fail: z.number().int(),
    }),
});
export const auditEntrySchema = z.object({
    id: z.string(),
    action: z.string(),
    actorName: z.string().nullable(),
    actorType: z.enum(['USER', 'INTEGRATOR', 'SYSTEM']),
    summary: z.string(),
    detail: z.record(z.unknown()).nullable(),
    createdAt: z.string(),
});
/** Everything the review screen needs, in one request — the operator should never watch
 *  four spinners resolve in sequence before they can start reading. */
export const documentDetailSchema = documentRowSchema.extend({
    chamberId: z.string(),
    structuredData: z.record(z.unknown()),
    /**
     * What the AI read off the certificate, where there was no submitted payload to read
     * instead. Always present so the review screen can state the basis the check ran on —
     * an operator glancing at a green panel must not assume more assurance than it carries.
     */
    extraction: extractionPanelSchema,
    /**
     * The specimen signatures on file, for a human to compare against the certificate.
     *
     * Deliberately not a similarity score: that is a biometric judgement with a
     * false-positive rate, made about someone's livelihood (D-016, Q7).
     */
    signatureComparison: z.object({
        signedBy: z.string().nullable(),
        specimens: z.array(z.object({
            id: z.string(),
            fullName: z.string(),
            position: z.string(),
            specimenUrl: z.string().nullable(),
            validUntil: z.string().nullable(),
            lapsed: z.boolean(),
            isNamedSignatory: z.boolean(),
        })),
    }),
    documentFileUrl: z.string().nullable(),
    documentFilename: z.string(),
    pageCount: z.number().int().nullable(),
    /**
     * Why a stamped PDF is missing, when it is.
     *
     * Null while stamping has simply not finished. Present means it FAILED — the difference
     * the review screen could not previously draw, so it told an operator a certificate was
     * "still being generated" indefinitely and offered a Retry that could not work.
     */
    stamping: z
        .object({
        error: z.string(),
        /** An ErrorCode when the engine refused this document; null when it did not. */
        code: z.string().nullable(),
        attempts: z.number().int(),
        /** True when retrying the same inputs will fail the same way. */
        deterministic: z.boolean(),
    })
        .nullable()
        .default(null),
    supportingDocuments: z.array(z.object({
        id: z.string(),
        category: z.string(),
        categoryLabel: z.string(),
        /** True when a machine guessed the category and no human has confirmed it. */
        categorySuggested: z.boolean().default(false),
        description: z.string().nullable().default(null),
        /** SUBMITTED with the request, ADDED by an operator, or PROVIDED on clarification. */
        origin: z.string().default('SUBMITTED'),
        filename: z.string(),
        fileUrl: z.string().nullable(),
        requestSignatureAndStamp: z.boolean(),
        stampedFileUrl: z.string().nullable(),
        /** Page dimensions in PDF points, for the click-to-place canvas. Null when the
         *  document is not stampable or could not be read. */
        pages: z
            .array(z.object({ width: z.number(), height: z.number() }))
            .nullable()
            .optional(),
    })),
    rejectionReason: z
        .object({ code: z.string(), label: z.string(), freeText: z.string().nullable() })
        .nullable(),
    clarification: z
        .object({
        items: z.array(z.object({ code: z.string(), label: z.string() })),
        freeText: z.string().nullable(),
        requestedAt: z.string(),
        respondBy: z.string().nullable(),
        respondedAt: z.string().nullable(),
        responseMessage: z.string().nullable(),
    })
        .nullable(),
    revocation: z.object({ reason: z.string(), revokedAt: z.string() }).nullable(),
    originalCertificateNo: z.string().nullable(),
    amendments: z.array(z.object({ id: z.string(), certificateNo: z.string().nullable() })),
    /** For an amendment: which one this is, against the chamber's limit (D-029). */
    amendmentStanding: z.object({ ordinal: z.number().int(), limit: z.number().int() }).nullable().default(null),
    checks: checkPanelSchema,
    audit: z.array(auditEntrySchema),
    /** Everything the Approve dialog needs, resolved server-side so the dialog opens ready. */
    approvalContext: z
        .object({
        stampingConfigId: z.string().nullable(),
        nextCertificateNoPreview: z.string().nullable(),
        slots: z.array(z.object({
            id: z.string(),
            slotType: z.string(),
            label: z.string().nullable(),
            page: z.number().int(),
            required: z.boolean(),
            defaultSealId: z.string().nullable(),
            resolvedPreview: z.string().nullable(),
        })),
        availableSeals: z.array(z.object({ id: z.string(), name: z.string(), imageUrl: z.string().nullable() })),
        signatureProfiles: z.array(z.object({
            id: z.string(),
            signatoryName: z.string(),
            designation: z.string(),
            imageUrl: z.string().nullable(),
            isDefault: z.boolean(),
        })),
        blockers: z.array(z.object({ code: z.string(), message: z.string() })),
    })
        .nullable(),
});
export const dashboardSchema = z.object({
    awaitingAction: z.object({
        total: z.number().int(),
        overdue: z.number().int(),
        dueToday: z.number().int(),
        dueThisWeek: z.number().int(),
        noTargetDate: z.number().int(),
    }),
    awaitingClarification: z.number().int(),
    pendingRegistrations: z.number().int(),
    pendingModificationRequests: z.number().int(),
    issuedThisMonth: z.number().int(),
    issuedLastMonth: z.number().int(),
    medianTurnaroundHours: z.number().nullable(),
    recentActivity: z.array(auditEntrySchema),
    /** Sparkline of issuance over the last 30 days. */
    throughput: z.array(z.object({ date: z.string(), issued: z.number().int() })),
});
//# sourceMappingURL=console.js.map